import { useConfirmCustomerIdentity } from '../features/conversations/hooks/use-confirm-customer-identity';

import { CapabilityGate } from './CapabilityGate';

export interface ConfirmCustomerIdentityModalProps {
  conversationId: string;
  /** Nome exibido do contato (para a frase de confirmação). */
  personName: string;
  open: boolean;
  onClose: () => void;
}

/**
 * ConfirmCustomerIdentityModal — Item 391-B (promoção por operador).
 *
 * O operador confirma que o contato É o titular do contrato. Isso promove o
 * vínculo a `operator_confirmed` e marca a identidade do canal verificada-via-
 * operador pelo período da política de validade do tenant (default 90d) — a IA
 * passa a operar com confiança `verified` (não re-pergunta). Gated por
 * `customer-registry-confirm`; ato auditado.
 */
export function ConfirmCustomerIdentityModal({
  conversationId,
  personName,
  open,
  onClose,
}: ConfirmCustomerIdentityModalProps): JSX.Element | null {
  const { confirm, isPending } = useConfirmCustomerIdentity(conversationId);

  if (!open) return null;

  const handleConfirm = (): void => {
    void confirm()
      .then(() => {
        onClose();
      })
      .catch(() => {
        // toast de erro já é emitido pelo hook; mantém o modal aberto.
      });
  };

  return (
    <CapabilityGate capability="customer-registry-confirm">
      <div
        role="dialog"
        aria-label="Confirmar titular"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      >
        <div
          className="w-full max-w-md rounded-lg p-6 shadow-xl"
          style={{ background: 'var(--bg-surface)' }}
        >
          <h2 className="mb-3 text-lg font-semibold">Confirmar titular</h2>
          <p className="text-sm" style={{ color: 'var(--fg-2)' }}>
            Você confirma que <strong>{personName}</strong> é o titular do contrato neste canal? A
            identidade passará a <strong>verificada por você</strong> (válida pelo período definido
            na política de validade do tenant) e a IA poderá atuar com confiança ampliada. Esta ação
            será registrada na auditoria.
          </p>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
              onClick={onClose}
              disabled={isPending}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleConfirm}
              disabled={isPending}
            >
              {isPending ? 'Confirmando…' : 'Confirmar titular'}
            </button>
          </div>
        </div>
      </div>
    </CapabilityGate>
  );
}
